// @flow
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import _ from 'shared/lib/gettext';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import StatusBadge from '~/app/ui/StatusBadge';
import { Section } from '~/app/ui/Page';
import { RowMenu } from '~/app/ui/Menu';
import { Table } from '~/app/ui/Table';
import { S, A, DARK } from '~/app/ui/tokens';
import { flashMessage as __flashMessage } from '~/app/actions';
import Scheduler from './Scheduler';
import useUpdateSchedule from './useUpdateSchedule';
import useCancelSchedule from './useCancelSchedule';
import useDeleteFailedSchedule from './useDeleteFailedSchedule';
import { getMinimumMinutesBetweenDeployments } from 'shared/lib/clientSettings';
import getDisplayPath from 'shared/lib/getDisplayPath';

const MINIMUM_MINUTES = getMinimumMinutesBetweenDeployments();

type Props = {
  classes: Object,
  scheduledDeployments: Array,
};

const ScheduledDeployment = (props: Props) => {
  const { scheduledDeployments, classes: c } = props;

  const [state, setState] = useState({
    cancelConfirm: null,
    deleteConfirm: null,
    isEditing: false,
    expandedRows: {},
    scheduleId: null,
    work: {}
  });
  const {
    cancelConfirm,
    deleteConfirm,
    isEditing,
    expandedRows,
    editing,
    scheduleId
  } = state;

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  const { updateSchedule } = useUpdateSchedule();
  const { cancelSchedule } = useCancelSchedule();
  const { deleteFailedSchedule } = useDeleteFailedSchedule();

  const handleClickEdit = (index, s) => {
    setState(x => ({
      ...x,
      editing: {
        ...s,
        position: {
          top: `${(44 + 64 * (index + 1)).toString()}px`,
          right: '40px'
        }
      },
      isEditing: true
    }));
  }

  const getCurrentActiveSchedules = () => {
    return scheduledDeployments.filter(schedule => (schedule.status === 'SCHEDULED' || schedule.status === 'NEW' || schedule.status === 'SCHEDULING'));
  }

  const handleUpdateSchedule = async (versionId, newDate, scheduleId) => {
    const currentSchedules = getCurrentActiveSchedules();

    const schedulesWithConflict = currentSchedules.filter(({ deployAt, version }) => {
      // not comparing time with itself
      if (versionId === version) return false;

      const targetDate = moment(parseInt(newDate));
      const startOfRange = moment(parseInt(deployAt)).subtract(MINIMUM_MINUTES, 'minutes');
      const endOfRange = moment(parseInt(deployAt)).add(MINIMUM_MINUTES, 'minutes')

      return targetDate.isBetween(startOfRange, endOfRange);
    });

    // validate if new schedule date is under restriction of minimum time between two deployments
    if (schedulesWithConflict && schedulesWithConflict.length > 0) {
      const message = _('ERROR_NO_SCHEDULE_WITHIN_MIN_MINS')
        .replace('{min}', MINIMUM_MINUTES)
        .replace('{version}', schedulesWithConflict[0].version);

      flashMessage(message, 'ERROR');
      return;
    }

    try {
      await updateSchedule(versionId, newDate, true, scheduleId);

      flashMessage(_('SUCCESS_UPDATE_SCHEDULE'));
      setState(x => ({
        ...x,
        isEditing: false
      }));
    } catch (error) {
      flashMessage(error.message, 'ERROR');
    }
  }

  // These throw on failure, so the confirmation stays open with the reason.
  const handleCancel = async () => {
    await cancelSchedule(cancelConfirm, scheduleId);
    flashMessage(_('SUCCESS_CANCEL_SCHEDULE'));
  }

  const handleDelete = async (_id) => {
    await deleteFailedSchedule(_id);
    flashMessage(_('SUCCESS_DELETE_FAILED'));
  }

  const statusOf = (s) => {
    const status = s.state === 'FAILED' ? 'FAILED' : (s.status || s.state || '');
    if (status === 'FAILED') return { tone: 'red', label: 'Failed' };
    if (status === 'SCHEDULED') return { tone: 'blue', label: 'Scheduled' };
    if (status === 'NEW' || status === 'SCHEDULING') return { tone: 'amber', label: 'Setting up' };
    const words = String(status).toLowerCase().replace(/_/g, ' ');
    return { tone: 'gray', label: words.charAt(0).toUpperCase() + words.slice(1) };
  };

  const rows = (scheduledDeployments || []).map((s, i) => {
    const at = moment(s.deployAt, 'x');
    const status = statusOf(s);
    const isOpen = !!expandedRows[s._id];
    const menu = [
      ...(s.state === 'SCHEDULED' ? [
        { key: 'edit', label: 'Change date and time…', icon: 'edit_calendar', onSelect: () => handleClickEdit(i, s) },
        { key: 'cancel', label: 'Cancel this deployment…', icon: 'event_busy', danger: true, onSelect: () => setState(x => ({ ...x, cancelConfirm: s.version, scheduleId: s.bucketName })) }
      ] : []),
      ...(s.state === 'FAILED' ? [
        { key: 'delete', label: 'Remove from the list…', icon: 'delete_outline', danger: true, onSelect: () => setState(x => ({ ...x, deleteConfirm: s })) }
      ] : [])
    ];
    return (
      <tr key={s._id || i} data-schedule={s.version}>
        <td className={c.colWhen}>
          <div className={c.when}>{at.format('ddd, MMM D, YYYY')}</div>
          <div className={c.muted}>{at.format('h:mm A')} · {at.fromNow()}</div>
        </td>
        <td className={`${c.colVersion} ${c.mono}`}>v{s.version}</td>
        <td>
          <div className={c.path}>{getDisplayPath(s.pages[0])}</div>
          {isOpen && s.pages.slice(1).map(p => <div className={c.path} key={p}>{getDisplayPath(p)}</div>)}
          {s.pages.length > 1 && (
            <button
              type="button"
              className={c.toggle}
              aria-expanded={isOpen}
              onClick={() => setState(x => ({ ...x, expandedRows: { ...expandedRows, [s._id]: !expandedRows[s._id] } }))}
            >
              {isOpen ? 'Show less' : `+ ${s.pages.length - 1} more`}
            </button>
          )}
          {s.notes && <div className={c.notes}>{s.notes}</div>}
        </td>
        <td className={c.colBy}>{s.scheduledBy ? s.scheduledBy.username : '—'}</td>
        <td className={c.colStatus}><StatusBadge tone={status.tone} label={status.label} /></td>
        <td className={c.colMenu}>
          <RowMenu label={`Actions for scheduled v${s.version}`} groups={[menu]} />
        </td>
      </tr>
    );
  });

  return (
    <Section title="Scheduled" hint="Deploy on their own at the set time" testId="scheduled-deployments" className={c.section}>
      <div className={c.wrap}>
        <Table label="Scheduled deployments">
          <thead>
            <tr>
              <th className={c.colWhen}>When</th>
              <th className={c.colVersion}>Version</th>
              <th>Pages</th>
              <th className={c.colBy}>Scheduled by</th>
              <th className={c.colStatus}>Status</th>
              <th className={c.colMenu}><span className={c.hidden}>Actions</span></th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
        {isEditing &&
          <Scheduler
            onCancel={() => setState(x => ({
              ...x,
              isEditing: false,
              editing: {}
            }))}
            scheduledId={editing._id}
            pages={editing.pages}
            versionNumber={editing.version}
            defaultDate={moment(parseInt(editing.deployAt))}
            position={editing.position}
            ctaText={_('CONFIRM_CHANGES')}
            onConfirm={date => handleUpdateSchedule(editing.version, date, editing.bucketName)}
          />
        }
      </div>
      {cancelConfirm &&
        <ConfirmDialog
          danger
          title={`Cancel the deployment of v${cancelConfirm}?`}
          confirmLabel="Cancel deployment"
          cancelLabel="Keep it"
          onConfirm={handleCancel}
          onClose={() => setState(x => ({
            ...x,
            cancelConfirm: null,
            scheduleId: null
          }))}
        >
          <p>Nothing goes live at the scheduled time. The changes go back to waiting under Ready to deploy.</p>
        </ConfirmDialog>
      }
      {deleteConfirm &&
        <ConfirmDialog
          danger
          title={`Remove the failed v${deleteConfirm.version} from the list?`}
          confirmLabel="Remove"
          onConfirm={() => handleDelete(deleteConfirm._id)}
          onClose={() => setState(x => ({ ...x, deleteConfirm: null }))}
        >
          <p>This only removes the record of the failed deployment. It doesn’t change the live site.</p>
        </ConfirmDialog>
      }
    </Section>
  );
}

const styles = {
  section: { marginBottom: 32 },
  wrap: { position: 'relative' },
  colWhen: { width: 210 },
  colVersion: { width: 110 },
  colBy: { width: 150, '@media (max-width: 900px)': { display: 'none' } },
  colStatus: { width: 140 },
  colMenu: { width: 52, textAlign: 'right' },
  hidden: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
  when: { fontWeight: 600 },
  muted: { fontSize: 13, color: S.muted },
  mono: { fontFamily: S.mono, fontSize: 13 },
  path: { fontFamily: S.mono, fontSize: 12, color: '#3A3D44', wordBreak: 'break-all', [DARK]: { color: S.fg } },
  notes: { marginTop: 4, fontSize: 13, color: S.muted },
  toggle: {
    all: 'unset',
    marginTop: 4,
    fontSize: 13,
    fontWeight: 600,
    color: S.muted,
    cursor: 'pointer',
    '&:hover': { color: S.fg },
    '&:focus-visible': A.focus
  }
};

export default injectSheet(styles)(ScheduledDeployment);
